import React, { useState, useRef } from "react";
import NavBar from "../component/NavBar";
import Footer from "../component/Footer";
import Phone from "../component/Phone";
import emailjs from "@emailjs/browser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faLocationDot,
  faClock,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

function Contact() {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const form = useRef();

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    emailjs
      .sendForm("service_gh7ck0g", "template_cice8fl", form.current, {
        publicKey: "7bwsohcrkchv3m5_G",
      })
      .then(
        () => {
          form.current.reset();
          setShowConfirmation(true);
          setIsSubmitting(false);
          setTimeout(() => {
            setShowConfirmation(false);
          }, 5000);
        },
        (error) => {
          setError("Failed to send message. Please try again or contact us directly.");
          setIsSubmitting(false);
        }
      );
  };

  return (
    <div className="bg-neutral-50 min-h-screen">
      <NavBar />
      <Phone />

      <div className="py-16 md:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h1 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Contact Us
            </h1>
            <p className="text-body-lg text-neutral-600 max-w-2xl mx-auto">
              Ready to take the first step towards mental wellness? Reach out to us today. We're here to help you on your journey.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-medium p-8 md:p-10">
                <h2 className="text-heading-md text-neutral-900 mb-6">Send Us a Message</h2>

                <form ref={form} onSubmit={sendEmail}>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-body-md font-semibold text-neutral-800 mb-2"
                      >
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="from_name"
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-neutral-900 placeholder:text-neutral-400"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-body-md font-semibold text-neutral-800 mb-2"
                      >
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="user_email"
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-neutral-900 placeholder:text-neutral-400"
                        placeholder="john.doe@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="phone"
                      className="block text-body-md font-semibold text-neutral-800 mb-2"
                    >
                      Phone Number <span className="text-neutral-500 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="user_phone"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-neutral-900 placeholder:text-neutral-400"
                      placeholder="(240) 610-3766"
                    />
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="subject"
                      className="block text-body-md font-semibold text-neutral-800 mb-2"
                    >
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-neutral-900 placeholder:text-neutral-400"
                      placeholder="How can we help you?"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="message"
                      className="block text-body-md font-semibold text-neutral-800 mb-2"
                    >
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      rows="6"
                      name="message"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all text-neutral-900 placeholder:text-neutral-400 resize-none"
                      placeholder="Please share any questions or information that will help us assist you better..."
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>

                  {showConfirmation && (
                    <div className="mt-4 p-4 bg-primary-50 border-l-4 border-primary-500 rounded-lg animate-fade-in flex items-start gap-3">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-primary-600 text-xl mt-0.5" />
                      <div>
                        <p className="text-primary-800 font-semibold">Message sent successfully!</p>
                        <p className="text-primary-700 text-body-sm mt-1">
                          Thank you for reaching out. We'll get back to you within 24-48 hours.
                        </p>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg animate-fade-in">
                      <p className="text-red-800 font-medium">{error}</p>
                    </div>
                  )}
                </form>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h3 className="text-heading-sm text-neutral-900 mb-6">Contact Information</h3>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faPhone} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-body-sm font-semibold text-neutral-800 mb-1">Phone</p>
                      <a href="tel:2406103766" className="text-body-md text-neutral-600 hover:text-primary-600 transition-colors">
                        (240) 610-3766
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faEnvelope} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-body-sm font-semibold text-neutral-800 mb-1">Email</p>
                      <a href="mailto:info@blissfuldayspsych.com" className="text-body-md text-neutral-600 hover:text-primary-600 transition-colors break-all">
                        info@blissfuldayspsych.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faLocationDot} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-body-sm font-semibold text-neutral-800 mb-1">Location</p>
                      <p className="text-body-md text-neutral-600">
                        12530 Fairwood Parkway<br />
                        Suite 102<br />
                        Bowie, MD 20720<br />
                        <span className="text-body-sm text-neutral-500">(Virtual Services)</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faClock} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-body-sm font-semibold text-neutral-800 mb-1">Office Hours</p>
                      <p className="text-body-md text-neutral-600">
                        Monday - Friday<br />
                        9:00 AM - 5:00 PM EST
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-xl p-6">
                <h3 className="text-heading-sm text-neutral-900 mb-3">Need Immediate Help?</h3>
                <p className="text-body-md text-neutral-700 mb-4">
                  If you're experiencing a mental health emergency, please contact:
                </p>
                <div className="space-y-2 text-body-sm">
                  <p className="font-semibold text-neutral-800">
                    National Suicide Prevention Lifeline:<br />
                    <a href="tel:988" className="text-primary-700 hover:text-primary-800">988</a>
                  </p>
                  <p className="font-semibold text-neutral-800">
                    Crisis Text Line:<br />
                    <span className="text-primary-700">Text HOME to 741741</span>
                  </p>
                  <p className="font-semibold text-neutral-800">
                    Emergency Services:<br />
                    <a href="tel:911" className="text-primary-700 hover:text-primary-800">911</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Contact;
